import * as React from 'react';
import {
  PublicClientApplication,
  type AccountInfo,
  type Configuration,
} from '@azure/msal-browser';
import { MsalProvider, useMsal } from '@azure/msal-react';
import { buildMsalConfig, readMsalEnv } from './msal-config';

/**
 * Auth context exposed to the rest of the app via `useAuth()`. The
 * underlying MSAL `PublicClientApplication` is memoized once per page load.
 *
 * Two modes:
 *  - Real MSAL — a real PublicClientApplication is created and the popup
 *    flow runs against the configured authority.
 *  - Dev bypass — when `VITE_DEV_AUTH_BYPASS=true`, the provider returns a
 *    fixed dev account and `getAccessToken()` returns a static dev token.
 *    Backend `AuthGuard` accepts the dev token via DEV_AUTH_BYPASS=true.
 */
export interface AuthContextValue {
  isAuthenticated: boolean;
  account: AccountInfo | DevAccount | null;
  login: () => Promise<void>;
  logout: () => Promise<void>;
  getAccessToken: () => Promise<string | null>;
}

export interface DevAccount {
  homeAccountId: string;
  environment: 'dev-local';
  tenantId: string;
  username: string;
  localAccountId: string;
  name: string;
}

const DEV_ACCOUNT: DevAccount = {
  homeAccountId: 'dev-oid-0001.dev-tid-partner-a',
  environment: 'dev-local',
  tenantId: 'dev-tid-partner-a',
  username: 'dev-user@local',
  localAccountId: 'dev-oid-0001',
  name: 'dev-user',
};

const AuthContext = React.createContext<AuthContextValue | null>(null);

/**
 * Lightweight dev token. Encoded as `header.payload.signature` so the
 * backend's `jwt.decode` happy-path matches; backend bypass mode skips
 * signature verification entirely.
 *
 * W83-F3 override — if `localStorage['senviro:dev-access-token']` is
 * present (seeded by the Playwright auth-roles fixture script), prefer
 * it. This lets a single Playwright spec exercise multiple Senviro
 * roles by swapping storage-state contexts.
 */
function buildDevToken(): string {
  if (typeof window !== 'undefined') {
    try {
      const override = window.localStorage.getItem('senviro:dev-access-token');
      if (override && override.includes('.')) {
        return override;
      }
    } catch {
      // localStorage unavailable (private mode, SSR) — fall through.
    }
  }
  const header = btoa(JSON.stringify({ alg: 'none', typ: 'JWT' }));
  const payload = btoa(
    JSON.stringify({
      oid: 'dev-oid-0001',
      tid: 'dev-tid-partner-a',
      email: 'dev-user@local',
      name: 'dev-user',
      preferred_username: 'dev-user@local',
      iss: 'https://login.microsoftonline.com/dev-tid-partner-a/v2.0',
      aud: 'dev-client-id',
      exp: Math.floor(Date.now() / 1000) + 3600,
      iat: Math.floor(Date.now() / 1000),
    }),
  );
  return `${header}.${payload}.dev-signature`;
}

interface BypassProviderProps {
  children: React.ReactNode;
}

/**
 * Persisted across reloads so the dev-bypass "logged out" state survives
 * a full page navigation. Cleared on `login()`.
 */
const DEV_LOGGED_OUT_KEY = 'senviro:dev-logged-out';

function readDevLoggedOut(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(DEV_LOGGED_OUT_KEY) === 'true';
  } catch {
    return false;
  }
}

function BypassAuthProvider({ children }: BypassProviderProps): JSX.Element {
  const [loggedOut, setLoggedOut] = React.useState<boolean>(() =>
    readDevLoggedOut(),
  );

  const value = React.useMemo<AuthContextValue>(
    () => ({
      isAuthenticated: !loggedOut,
      account: loggedOut ? null : DEV_ACCOUNT,
      login: async () => {
        try {
          window.localStorage.removeItem(DEV_LOGGED_OUT_KEY);
        } catch {
          /* localStorage unavailable */
        }
        setLoggedOut(false);
      },
      logout: async () => {
        try {
          window.localStorage.setItem(DEV_LOGGED_OUT_KEY, 'true');
          // Also drop the per-role override + any cached auth-derived state
          // so the next login picks up a clean session.
          window.localStorage.removeItem('senviro:dev-access-token');
        } catch {
          /* localStorage unavailable */
        }
        setLoggedOut(true);
      },
      getAccessToken: async () => (loggedOut ? null : buildDevToken()),
    }),
    [loggedOut],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

interface MsalAuthBridgeProps {
  children: React.ReactNode;
}

function MsalAuthBridge({ children }: MsalAuthBridgeProps): JSX.Element {
  const { instance, accounts } = useMsal();
  const account = accounts[0] ?? null;
  const env = readMsalEnv();

  const value = React.useMemo<AuthContextValue>(
    () => ({
      isAuthenticated: account !== null,
      account,
      login: async () => {
        // `prompt: 'select_account'` forces Microsoft to render the
        // account-picker on every sign-in instead of silently
        // resuming via the SSO cookie. One extra click for a returning
        // single-account user is the right trade for letting people
        // switch accounts without clearing browser state.
        await instance.loginPopup({
          scopes: env.scopes,
          prompt: 'select_account',
        });
      },
      logout: async () => {
        await instance.logoutPopup({ account: account ?? undefined });
      },
      getAccessToken: async () => {
        if (!account) return null;
        try {
          const result = await instance.acquireTokenSilent({
            account,
            scopes: env.scopes,
          });
          return result.idToken ?? result.accessToken ?? null;
        } catch {
          const result = await instance.acquireTokenPopup({
            account,
            scopes: env.scopes,
          });
          return result.idToken ?? result.accessToken ?? null;
        }
      },
    }),
    [instance, account, env.scopes],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

let memoizedClient: PublicClientApplication | null = null;
let memoizedInit: Promise<void> | null = null;
function getClient(config: Configuration): PublicClientApplication {
  if (!memoizedClient) {
    memoizedClient = new PublicClientApplication(config);
  }
  return memoizedClient;
}
// @azure/msal-browser@3 requires `await instance.initialize()` before ANY
// other MSAL API call. msal-react@2 does NOT auto-initialize, so consumers
// that call `acquireTokenSilent` on mount throw
// `BrowserAuthError: uninitialized_public_client_application` (Sentry
// SENVIRO-WEB-4). Cached so StrictMode's double-mount doesn't re-initialize.
function initClient(client: PublicClientApplication): Promise<void> {
  if (!memoizedInit) {
    memoizedInit = client.initialize();
  }
  return memoizedInit;
}

export interface AuthProviderProps {
  children: React.ReactNode;
}

interface InitializedMsalProviderProps {
  client: PublicClientApplication;
  children: React.ReactNode;
}

function InitializedMsalProvider({
  client,
  children,
}: InitializedMsalProviderProps): JSX.Element | null {
  const [ready, setReady] = React.useState(false);
  React.useEffect(() => {
    let cancelled = false;
    initClient(client).then(
      () => {
        if (!cancelled) setReady(true);
      },
      () => {
        if (!cancelled) setReady(true);
      },
    );
    return () => {
      cancelled = true;
    };
  }, [client]);
  if (!ready) return null;
  return (
    <MsalProvider instance={client}>
      <MsalAuthBridge>{children}</MsalAuthBridge>
    </MsalProvider>
  );
}

export function AuthProvider({ children }: AuthProviderProps): JSX.Element {
  const env = readMsalEnv();
  if (env.devBypass) {
    return <BypassAuthProvider>{children}</BypassAuthProvider>;
  }
  const client = getClient(buildMsalConfig());
  return <InitializedMsalProvider client={client}>{children}</InitializedMsalProvider>;
}

export function useAuth(): AuthContextValue {
  const ctx = React.useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth() must be used inside <AuthProvider>');
  }
  return ctx;
}

// Test helper — re-export so unit tests can construct a context without
// MSAL or dev-bypass plumbing.
export const __test__ = {
  AuthContext,
  DEV_ACCOUNT,
  buildDevToken,
  DEV_LOGGED_OUT_KEY,
};
